import React, { useState, useMemo, useRef, useEffect } from 'react'
import { CampaignPerformanceData, ChartGranularity } from '../types'
import { useReportHub } from '../../../Contexts/useReportHub'
import { Button, Fa } from '@eltoro-ui/components'

interface TooltipData {
  x: number
  y: number
  date: string
  impressions: number
  clicks: number
  ctr: number
}

export const ReportHubCampaignPerformanceChart = () => {
  const {
    chartGranularity,
    updateChartGranularity,
    overviewStats,
  } = useReportHub()
  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  const [hoveredLine, setHoveredLine] = useState<
    'impressions' | 'clicks' | 'ctr' | null
  >(null)
  const [visibleLines, setVisibleLines] = useState<{
    impressions: boolean
    clicks: boolean
    ctr: boolean
  }>({
    impressions: true,
    clicks: true,
    ctr: true,
  })
  const [containerWidth, setContainerWidth] = useState(500)
  const [currentPage, setCurrentPage] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const ITEMS_PER_PAGE = 7

  // Process all stats data into chart format
  const allData = useMemo((): CampaignPerformanceData[] => {
    if (!overviewStats?.statsData?.results) {
      return []
    }

    // First, group daily data by date and sum metrics across all campaigns/order lines
    const dailyGroups: Record<
      string,
      { impressions: number; clicks: number }
    > = {}

    // Iterate through each campaign/order line
    overviewStats.statsData.results.forEach((campaignOrOrderLine: any) => {
      // Each campaign/order line has a results array with daily data
      if (
        campaignOrOrderLine.results &&
        Array.isArray(campaignOrOrderLine.results)
      ) {
        campaignOrOrderLine.results.forEach((dailyResult: any) => {
          // Use the start date as the key (format: "2025-08-16T05:00:00Z")
          const dateKey = dailyResult.start?.split('T')[0] // Extract just the date part
          if (!dateKey) return // Skip if no start date

          if (!dailyGroups[dateKey]) {
            dailyGroups[dateKey] = { impressions: 0, clicks: 0 }
          }
          dailyGroups[dateKey].impressions += dailyResult.imps || 0
          dailyGroups[dateKey].clicks += dailyResult.clicks || 0
        })
      }
    })

    // Convert to daily chart data format and sort by date
    const dailyData = Object.entries(dailyGroups)
      .map(([date, metrics]) => {
        const impressions = isFinite(metrics.impressions)
          ? metrics.impressions
          : 0
        const clicks = isFinite(metrics.clicks) ? metrics.clicks : 0
        const ctr = impressions > 0 ? clicks / impressions : 0

        return {
          date,
          impressions,
          clicks,
          ctr: isFinite(ctr) ? ctr : 0,
        }
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    // If granularity is 'day', return daily data as-is
    if (chartGranularity === 'day') {
      return dailyData
    }

    // Aggregate daily data based on granularity
    const aggregatedGroups: Record<
      string,
      { impressions: number; clicks: number; dates: string[] }
    > = {}

    dailyData.forEach((dailyEntry) => {
      const date = new Date(dailyEntry.date)
      let groupKey: string

      if (chartGranularity === 'week') {
        // Group by week (Monday start)
        const startOfWeek = new Date(date)
        const dayOfWeek = startOfWeek.getDay()
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Monday = 1, Sunday = 0
        startOfWeek.setDate(startOfWeek.getDate() + diff)
        const [weekDatePart] = startOfWeek.toISOString().split('T')
        groupKey = weekDatePart
      } else {
        // Group by month
        groupKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1,
        ).padStart(2, '0')}-01`
      }

      if (!aggregatedGroups[groupKey]) {
        aggregatedGroups[groupKey] = { impressions: 0, clicks: 0, dates: [] }
      }

      aggregatedGroups[groupKey].impressions += dailyEntry.impressions
      aggregatedGroups[groupKey].clicks += dailyEntry.clicks
      aggregatedGroups[groupKey].dates.push(dailyEntry.date)
    })

    // Convert aggregated groups to chart data format
    return Object.entries(aggregatedGroups)
      .map(([groupDate, metrics]) => {
        const impressions = isFinite(metrics.impressions)
          ? metrics.impressions
          : 0
        const clicks = isFinite(metrics.clicks) ? metrics.clicks : 0
        const ctr = impressions > 0 ? clicks / impressions : 0

        return {
          date: groupDate,
          impressions,
          clicks,
          ctr: isFinite(ctr) ? ctr : 0,
        }
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }, [overviewStats, chartGranularity])

  // Paginated data for display (limit to 7 items)
  const data = useMemo(() => {
    const startIndex = currentPage * ITEMS_PER_PAGE
    return allData.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [allData, currentPage])

  // Navigation functions
  const totalPages =
    allData.length > 0 ? Math.ceil(allData.length / ITEMS_PER_PAGE) : 1
  const lastPageIndex = Math.max(0, totalPages - 1)
  const canGoBack = currentPage > 0
  const canGoForward = currentPage < totalPages - 1

  const goToPreviousPage = () => {
    if (canGoBack) {
      setCurrentPage((prev) => prev - 1)
    }
  }

  const goToNextPage = () => {
    if (canGoForward) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  // Set to most recent page when granularity changes or data first loads
  useEffect(() => {
    setCurrentPage(lastPageIndex)
  }, [chartGranularity, lastPageIndex])

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth
        setContainerWidth(Math.max(300, width - 48)) // Subtract padding, minimum 300px
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Chart dimensions - responsive
  const chartWidth = containerWidth
  const chartHeight = Math.max(250, Math.min(350, containerWidth * 0.6)) // Responsive height
  const padding = { top: 40, right: 80, bottom: 60, left: 80 }
  const plotWidth = chartWidth - padding.left - padding.right
  const plotHeight = chartHeight - padding.top - padding.bottom

  // Calculate scales - based on currently visible data (paginated view), respects visible metrics
  const scales = useMemo(() => {
    if (data.length === 0) {
      return {
        impressions: { min: 0, max: 1 },
        clicks: { min: 0, max: 1 },
        ctr: { min: 0, max: 1 },
      }
    }

    // Calculate min and max based on CURRENTLY VISIBLE data, only for visible metrics
    const impressionsValues = visibleLines.impressions
      ? data.map((d) => d.impressions)
      : []
    const clicksValues = visibleLines.clicks ? data.map((d) => d.clicks) : []
    const ctrValues = visibleLines.ctr ? data.map((d) => d.ctr) : []

    const minImpressions =
      impressionsValues.length > 0 ? Math.min(...impressionsValues) : 0
    const maxImpressions =
      impressionsValues.length > 0 ? Math.max(...impressionsValues) : 0
    const minClicks = clicksValues.length > 0 ? Math.min(...clicksValues) : 0
    const maxClicks = clicksValues.length > 0 ? Math.max(...clicksValues) : 0
    const minCTR = ctrValues.length > 0 ? Math.min(...ctrValues) : 0
    const maxCTR = ctrValues.length > 0 ? Math.max(...ctrValues) : 0

    // Add 10% headroom above and below for better visual spacing
    const headroom = 0.1

    const roundToNiceNumber = (value: number) => {
      if (value <= 0) return 0

      // Find the magnitude (power of 10)
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)))

      // Normalize value to 1-10 range
      const normalized = value / magnitude

      // Round to nice number (1, 2, 5, 10)
      let nice
      if (normalized <= 1) nice = 1
      else if (normalized <= 2) nice = 2
      else if (normalized <= 5) nice = 5
      else nice = 10

      return nice * magnitude
    }

    const calculateRange = (min: number, max: number, skipRounding = false) => {
      // If min equals max, add some padding
      if (min === max) {
        const padding = max === 0 ? 1 : max * 0.1
        return { min: Math.max(0, min - padding), max: max + padding }
      }

      const range = max - min
      const paddedMin = Math.max(0, min - range * headroom)
      const paddedMax = max + range * headroom

      // For very small values (like CTR), skip rounding to avoid over-scaling
      if (skipRounding || paddedMax < 0.1) {
        return {
          min: paddedMin,
          max: paddedMax,
        }
      }

      return {
        min: roundToNiceNumber(paddedMin),
        max: roundToNiceNumber(paddedMax),
      }
    }

    return {
      impressions: visibleLines.impressions
        ? calculateRange(minImpressions, maxImpressions)
        : { min: 0, max: 1 },
      clicks: visibleLines.clicks
        ? calculateRange(minClicks, maxClicks)
        : { min: 0, max: 1 },
      ctr: visibleLines.ctr
        ? calculateRange(minCTR, maxCTR, true) // Skip rounding for CTR to get precise scaling
        : { min: 0, max: 0.01 },
    }
  }, [data, visibleLines])

  // Generate SVG path for line
  const generatePath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) return ''

    // Filter out any points with NaN or non-finite values
    const validPoints = points.filter(
      (point) => isFinite(point.x) && isFinite(point.y),
    )

    if (validPoints.length === 0) return ''

    const path = validPoints.reduce((acc, point, index) => {
      const command = index === 0 ? 'M' : 'L'
      return `${acc} ${command} ${point.x} ${point.y}`
    }, '')

    return path.trim()
  }

  // Calculate points for each line using dynamic min/max scaling
  const lineData = useMemo(() => {
    if (data.length === 0) {
      return {
        impressions: { points: [], path: '' },
        clicks: { points: [], path: '' },
        ctr: { points: [], path: '' },
      }
    }

    const xStep = data.length > 1 ? plotWidth / (data.length - 1) : 0

    const impressionsPoints = data.map((d, index) => {
      const x = data.length === 1 ? plotWidth / 2 : index * xStep
      const normalizedValue = isFinite(d.impressions)
        ? d.impressions
        : scales.impressions.min
      const range = scales.impressions.max - scales.impressions.min
      const ratio =
        range > 0
          ? Math.max(
              0,
              Math.min(1, (normalizedValue - scales.impressions.min) / range),
            )
          : 0.5
      const y = plotHeight - ratio * plotHeight
      return {
        x: isFinite(x) ? x : 0,
        y: isFinite(y) ? Math.max(0, Math.min(plotHeight, y)) : plotHeight,
      }
    })

    const clicksPoints = data.map((d, index) => {
      const x = data.length === 1 ? plotWidth / 2 : index * xStep
      const normalizedValue = isFinite(d.clicks) ? d.clicks : scales.clicks.min
      const range = scales.clicks.max - scales.clicks.min
      const ratio =
        range > 0
          ? Math.max(
              0,
              Math.min(1, (normalizedValue - scales.clicks.min) / range),
            )
          : 0.5
      const y = plotHeight - ratio * plotHeight
      return {
        x: isFinite(x) ? x : 0,
        y: isFinite(y) ? Math.max(0, Math.min(plotHeight, y)) : plotHeight,
      }
    })

    const ctrPoints = data.map((d, index) => {
      const x = data.length === 1 ? plotWidth / 2 : index * xStep
      const normalizedValue = isFinite(d.ctr) ? d.ctr : scales.ctr.min
      const range = scales.ctr.max - scales.ctr.min
      const ratio =
        range > 0
          ? Math.max(0, Math.min(1, (normalizedValue - scales.ctr.min) / range))
          : 0.5
      const y = plotHeight - ratio * plotHeight
      return {
        x: isFinite(x) ? x : 0,
        y: isFinite(y) ? Math.max(0, Math.min(plotHeight, y)) : plotHeight,
      }
    })

    return {
      impressions: {
        points: impressionsPoints,
        path: generatePath(impressionsPoints),
      },
      clicks: { points: clicksPoints, path: generatePath(clicksPoints) },
      ctr: { points: ctrPoints, path: generatePath(ctrPoints) },
    }
  }, [data, scales, plotWidth, plotHeight])

  // Handle mouse events for data points
  const handlePointMouseEnter = (
    event: React.MouseEvent<SVGCircleElement>,
    dataIndex: number,
    metric: 'impressions' | 'clicks' | 'ctr',
  ) => {
    const dataPoint = data[dataIndex]
    if (dataPoint) {
      setHoveredLine(metric)
      setTooltip({
        x: event.clientX,
        y: event.clientY,
        date: dataPoint.date,
        impressions: dataPoint.impressions,
        clicks: dataPoint.clicks,
        ctr: dataPoint.ctr,
      })
    }
  }

  const handlePointMouseLeave = () => {
    setHoveredLine(null)
    setTooltip(null)
  }

  // Handle legend line mouse events
  const handleLineMouseEnter = (metric: 'impressions' | 'clicks' | 'ctr') => {
    setHoveredLine(metric)
  }

  const handleLineMouseLeave = () => {
    setHoveredLine(null)
  }

  // Handle legend click to toggle line visibility
  const handleLegendClick = (metric: 'impressions' | 'clicks' | 'ctr') => {
    setVisibleLines((prev) => {
      const newVisibleLines = { ...prev, [metric]: !prev[metric] }

      // If all lines are hidden, show only the clicked line
      const visibleCount = Object.values(newVisibleLines).filter(Boolean).length
      if (visibleCount === 0) {
        return {
          impressions: metric === 'impressions',
          clicks: metric === 'clicks',
          ctr: metric === 'ctr',
        }
      }

      return newVisibleLines
    })
  }

  // Format functions
  const formatImpressions = (value: number) =>
    value.toLocaleString('en-US', { maximumFractionDigits: 0 })
  const formatClicks = (value: number) =>
    value.toLocaleString('en-US', { maximumFractionDigits: 0 })
  const formatCTR = (value: number) => `${(value * 100).toFixed(2)}%`
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)

    if (chartGranularity === 'week') {
      // For weeks, show "Week of MM/DD"
      return `Week of ${date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
      })}`
    } else if (chartGranularity === 'month') {
      // For months, show "MMM YYYY"
      return date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      })
    } else {
      // For days, show "MM/DD/YYYY"
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
      })
    }
  }

  // Calculate dynamic position for left axis label based on max number width
  const leftAxisLabelX = useMemo(() => {
    if (!visibleLines.impressions) return 15

    // Find the longest formatted impression value
    const maxValue = scales.impressions.max
    const formattedMax = formatImpressions(maxValue)

    // Estimate character width (approximate 7 pixels per character for 12px font)
    const estimatedWidth = formattedMax.length * 7

    // Position label further left based on number width
    // Account for rotated text: need enough space for the full text width
    // Minimum position should account for the text height (12px font ~= 12px height)
    const minPosition = 15 // Minimum to avoid clipping
    const idealPosition = 65 - estimatedWidth
    const position = Math.max(minPosition, idealPosition)

    return position
  }, [scales.impressions.max, visibleLines.impressions])

  return (
    <section
      ref={containerRef}
      className="ReportHubCampaignPerformanceChart bg-base border-tint-gray-300 rounded-lg border p-6"
      aria-labelledby="chart-title"
      role="img"
      aria-describedby="chart-description"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex flex-wrap items-center gap-4">
          <h3
            id="chart-title"
            className="text-tint-gray-900 text-lg font-semibold"
          >
            Campaign Performance
          </h3>

          {/* Navigation arrows */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                onClick={goToPreviousPage}
                disabled={!canGoBack}
                size="s"
                aria-label="Previous period"
                className="!h-8 !w-8 !p-0"
                iconOnly={<Fa icon="chevron-left" size={0.75} />}
              />

              <span className="text-tint-gray-600 text-sm">
                {currentPage + 1} of {totalPages}
              </span>

              <Button
                onClick={goToNextPage}
                disabled={!canGoForward}
                size="s"
                aria-label="Next period"
                className="!h-8 !w-8 !p-0"
                iconOnly={<Fa icon="chevron-right" size={0.75} />}
              />
            </div>
          )}
        </div>

        <div
          id="Granularity Buttons"
          className="flex flex-wrap items-center gap-1"
        >
          {([
            ['Day', 'day'],
            ['Week', 'week'],
            ['Month', 'month'],
          ] as [string, ChartGranularity][]).map(([label, value]) => (
            <Button
              key={value}
              size="s"
              className="w-[60px] min-w-[60px] !px-1 !py-0 !text-xs normal-case !leading-none"
              kind={chartGranularity === value ? 'primary' : 'default'}
              aria-label={`Set granularity to ${label}`}
              aria-pressed={chartGranularity === value}
              onClick={() => updateChartGranularity(value)}
              disabled={(data?.length ?? 0) === 0}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div id="chart-description" className="sr-only">
        Line chart showing campaign performance over time with three metrics:
        Impressions in millions, Clicks in thousands, and Click-through rate as
        percentage.{' '}
        {data.length > 0
          ? `Data spans from ${data[0]?.date} to ${
              data[data.length - 1]?.date
            }.`
          : 'No data available.'}
      </div>

      {data.length === 0 ? (
        <div className="text-grey-500 flex h-64 items-center justify-center">
          <p>No chart data available yet.</p>
        </div>
      ) : (
        <div className="relative w-full overflow-hidden">
          <svg
            width="100%"
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="w-full"
            preserveAspectRatio="xMidYMid meet"
            role="presentation"
            aria-hidden="true"
            focusable="false"
          >
            {/* Grid lines */}
            <defs>
              <pattern
                id="grid"
                width="1"
                height="1"
                patternUnits="userSpaceOnUse"
              >
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                  stroke="#e5e7eb"
                  strokeWidth="0.5"
                />
                <line
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                  stroke="#e5e7eb"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>

            {/* Vertical grid lines - lighter, dashed */}
            {data.map((_, index) => (
              <line
                key={index}
                x1={
                  padding.left +
                  (data.length > 1
                    ? (index / (data.length - 1)) * plotWidth
                    : plotWidth / 2)
                }
                y1={padding.top}
                x2={
                  padding.left +
                  (data.length > 1
                    ? (index / (data.length - 1)) * plotWidth
                    : plotWidth / 2)
                }
                y2={padding.top + plotHeight}
                stroke="var(--chart-grid-line)"
                strokeWidth="0.5"
                strokeDasharray="3,3"
              />
            ))}

            {/* Horizontal grid lines - lighter, dashed */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
              <line
                key={ratio}
                x1={padding.left}
                y1={padding.top + ratio * plotHeight}
                x2={padding.left + plotWidth}
                y2={padding.top + ratio * plotHeight}
                stroke="var(--chart-grid-line)"
                strokeWidth="0.5"
                strokeDasharray="3,3"
              />
            ))}

            {/* Chart border - solid outer lines */}
            <rect
              x={padding.left}
              y={padding.top}
              width={plotWidth}
              height={plotHeight}
              fill="none"
              stroke="#9ca3af"
              strokeWidth="1"
            />

            {/* Impressions line */}
            {visibleLines.impressions && (
              <path
                d={lineData.impressions.path}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="2"
                opacity={hoveredLine && hoveredLine !== 'impressions' ? 0.3 : 1}
                transform={`translate(${padding.left}, ${padding.top})`}
                onMouseEnter={() => handleLineMouseEnter('impressions')}
                onMouseLeave={handleLineMouseLeave}
                className="cursor-pointer transition-all duration-200"
              />
            )}

            {/* Clicks line */}
            {visibleLines.clicks && (
              <path
                d={lineData.clicks.path}
                fill="none"
                stroke="#EF4444"
                strokeWidth="2"
                opacity={hoveredLine && hoveredLine !== 'clicks' ? 0.3 : 1}
                transform={`translate(${padding.left}, ${padding.top})`}
                onMouseEnter={() => handleLineMouseEnter('clicks')}
                onMouseLeave={handleLineMouseLeave}
                className="cursor-pointer transition-all duration-200"
              />
            )}

            {/* CTR line */}
            {visibleLines.ctr && (
              <path
                d={lineData.ctr.path}
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                opacity={hoveredLine && hoveredLine !== 'ctr' ? 0.3 : 1}
                transform={`translate(${padding.left}, ${padding.top})`}
                onMouseEnter={() => handleLineMouseEnter('ctr')}
                onMouseLeave={handleLineMouseLeave}
                className="cursor-pointer transition-all duration-200"
              />
            )}

            {/* Data points */}
            {visibleLines.impressions &&
              lineData.impressions.points.map((point, index) => (
                <circle
                  key={`impressions-${index}`}
                  cx={padding.left + point.x}
                  cy={padding.top + point.y}
                  r="4"
                  fill="#3B82F6"
                  opacity={
                    hoveredLine && hoveredLine !== 'impressions' ? 0.3 : 1
                  }
                  className="cursor-pointer transition-all duration-200"
                  onMouseEnter={(e) =>
                    handlePointMouseEnter(e, index, 'impressions')
                  }
                  onMouseLeave={handlePointMouseLeave}
                />
              ))}

            {visibleLines.clicks &&
              lineData.clicks.points.map((point, index) => (
                <circle
                  key={`clicks-${index}`}
                  cx={padding.left + point.x}
                  cy={padding.top + point.y}
                  r="4"
                  fill="#EF4444"
                  opacity={hoveredLine && hoveredLine !== 'clicks' ? 0.3 : 1}
                  className="cursor-pointer transition-all duration-200"
                  onMouseEnter={(e) =>
                    handlePointMouseEnter(e, index, 'clicks')
                  }
                  onMouseLeave={handlePointMouseLeave}
                />
              ))}

            {visibleLines.ctr &&
              lineData.ctr.points.map((point, index) => (
                <circle
                  key={`ctr-${index}`}
                  cx={padding.left + point.x}
                  cy={padding.top + point.y}
                  r="4"
                  fill="#10B981"
                  opacity={hoveredLine && hoveredLine !== 'ctr' ? 0.3 : 1}
                  className="cursor-pointer transition-all duration-200"
                  onMouseEnter={(e) => handlePointMouseEnter(e, index, 'ctr')}
                  onMouseLeave={handlePointMouseLeave}
                />
              ))}

            {/* Y-axis labels - Impressions (left) */}
            {visibleLines.impressions &&
              [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                const range = scales.impressions.max - scales.impressions.min
                const value = scales.impressions.max - ratio * range
                return (
                  <text
                    key={`impressions-${ratio}`}
                    x={padding.left - 10}
                    y={padding.top + ratio * plotHeight + 5}
                    textAnchor="end"
                    fontSize="12"
                    fill="var(--tint-gray-900)"
                  >
                    {formatImpressions(value)}
                  </text>
                )
              })}

            {/* Y-axis labels - Stacked Clicks and CTR (right side) */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
              const clicksRange = scales.clicks.max - scales.clicks.min
              const clicksValue = scales.clicks.max - ratio * clicksRange
              const ctrRange = scales.ctr.max - scales.ctr.min
              const ctrValue = scales.ctr.max - ratio * ctrRange
              return (
                <g key={`stacked-${ratio}`}>
                  {/* Clicks label (top) - only show if clicks visible */}
                  {visibleLines.clicks && (
                    <text
                      x={chartWidth - padding.right + 35}
                      y={padding.top + ratio * plotHeight - 5}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#EF4444"
                      fontWeight="500"
                    >
                      {formatClicks(clicksValue)}
                    </text>
                  )}
                  {/* CTR label (bottom) - only show if CTR visible */}
                  {visibleLines.ctr && (
                    <text
                      x={chartWidth - padding.right + 35}
                      y={padding.top + ratio * plotHeight + 10}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#10B981"
                      fontWeight="500"
                    >
                      {formatCTR(ctrValue)}
                    </text>
                  )}
                </g>
              )
            })}

            {/* X-axis labels */}
            {data.map((d, index) => (
              <text
                key={index}
                x={
                  padding.left +
                  (data.length > 1
                    ? (index / (data.length - 1)) * plotWidth
                    : plotWidth / 2)
                }
                y={chartHeight - 20}
                textAnchor="middle"
                fontSize="12"
                fill="var(--tint-gray-900)"
              >
                {formatDate(d.date)}
              </text>
            ))}

            {/* Axis labels */}
            {visibleLines.impressions && (
              <text
                x={leftAxisLabelX}
                y={chartHeight / 2}
                textAnchor="middle"
                fontSize="12"
                className="fill-current"
                transform={`rotate(-90, ${leftAxisLabelX}, ${chartHeight / 2})`}
              >
                Impressions (M)
              </text>
            )}

            {(visibleLines.clicks || visibleLines.ctr) && (
              <text
                x={chartWidth - 35}
                y={chartHeight / 2}
                textAnchor="middle"
                fontSize="12"
                className="fill-current"
                transform={`rotate(90, ${chartWidth - 20}, ${
                  chartHeight / 2 + 9
                })`}
              >
                {visibleLines.clicks && visibleLines.ctr
                  ? 'Clicks (K) | CTR (%)'
                  : visibleLines.clicks
                  ? 'Clicks (K)'
                  : 'CTR (%)'}
              </text>
            )}
          </svg>

          {/* Legend with keyboard navigation support */}
          <div
            className="mt-4 flex justify-center space-x-8"
            role="list"
            aria-label="Chart legend"
          >
            <div
              className={`flex cursor-pointer items-center space-x-2 transition-all duration-200 ${
                visibleLines.impressions ? 'opacity-100' : 'opacity-50'
              }`}
              role="listitem"
              tabIndex={0}
              title={`Click to ${
                visibleLines.impressions ? 'hide' : 'show'
              } impressions line`}
              aria-label={`Impressions data series, shown in blue line with circles. Click to ${
                visibleLines.impressions ? 'hide' : 'show'
              }.`}
              onClick={() => handleLegendClick('impressions')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleLegendClick('impressions')
                }
              }}
            >
              <svg
                width="16"
                height="9"
                className="flex-shrink-0"
                aria-hidden="true"
              >
                <line
                  x1="2"
                  y1="6"
                  x2="14"
                  y2="6"
                  stroke="#3B82F6"
                  strokeWidth="2"
                  opacity={visibleLines.impressions ? 1 : 0.5}
                />
                <circle
                  cx="8"
                  cy="6"
                  r="3"
                  fill="#3B82F6"
                  opacity={visibleLines.impressions ? 1 : 0.5}
                />
              </svg>
              <span
                className={`text-[1rem] font-medium ${
                  visibleLines.impressions ? 'text-blue-600' : 'line-through'
                }`}
              >
                Impressions
              </span>
            </div>
            <div
              className={`flex cursor-pointer items-center space-x-2 transition-all duration-200 ${
                visibleLines.clicks ? 'opacity-100' : 'opacity-50'
              }`}
              role="listitem"
              tabIndex={0}
              title={`Click to ${
                visibleLines.clicks ? 'hide' : 'show'
              } clicks line`}
              aria-label={`Clicks data series, shown in red line with circles. Click to ${
                visibleLines.clicks ? 'hide' : 'show'
              }.`}
              onClick={() => handleLegendClick('clicks')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleLegendClick('clicks')
                }
              }}
            >
              <svg
                width="16"
                height="9"
                className="flex-shrink-0"
                aria-hidden="true"
              >
                <line
                  x1="2"
                  y1="6"
                  x2="14"
                  y2="6"
                  stroke="#EF4444"
                  strokeWidth="2"
                  opacity={visibleLines.clicks ? 1 : 0.5}
                />
                <circle
                  cx="8"
                  cy="6"
                  r="3"
                  fill="#EF4444"
                  opacity={visibleLines.clicks ? 1 : 0.5}
                />
              </svg>
              <span
                className={`text-[1rem] font-medium ${
                  visibleLines.clicks ? 'text-red-600' : 'line-through'
                }`}
              >
                Clicks
              </span>
            </div>
            <div
              className={`flex cursor-pointer items-center space-x-2 transition-all duration-200 ${
                visibleLines.ctr ? 'opacity-100' : 'opacity-50'
              }`}
              role="listitem"
              tabIndex={0}
              title={`Click to ${visibleLines.ctr ? 'hide' : 'show'} CTR line`}
              aria-label={`Click-through rate data series, shown in green line with circles. Click to ${
                visibleLines.ctr ? 'hide' : 'show'
              }.`}
              onClick={() => handleLegendClick('ctr')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleLegendClick('ctr')
                }
              }}
            >
              <svg
                width="16"
                height="9"
                className="flex-shrink-0"
                aria-hidden="true"
              >
                <line
                  x1="2"
                  y1="6"
                  x2="14"
                  y2="6"
                  stroke="#10B981"
                  strokeWidth="2"
                  opacity={visibleLines.ctr ? 1 : 0.5}
                />
                <circle
                  cx="8"
                  cy="6"
                  r="3"
                  fill="#10B981"
                  opacity={visibleLines.ctr ? 1 : 0.5}
                />
              </svg>
              <span
                className={`text-[1rem] font-medium ${
                  visibleLines.ctr ? 'text-green-600' : 'line-through'
                }`}
              >
                CTR
              </span>
            </div>
          </div>

          {/* Helper text for legend interaction */}
          <div className="mt-2 text-center text-xs">
            <p className="text-tint-gray-800 italic">
              Click on legend items to show/hide chart lines
            </p>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          className="bg-base pointer-events-none fixed z-50 rounded-lg border border-gray-300 p-3 shadow-lg dark:border-gray-600 dark:bg-gray-800"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y - 80,
          }}
          role="tooltip"
          aria-live="polite"
        >
          <div className="mb-1 text-sm font-medium">
            {formatDate(tooltip.date)}
          </div>
          {visibleLines.impressions && (
            <div className="text-sm text-blue-600 dark:text-blue-400">
              <strong>Impressions:</strong>{' '}
              {formatImpressions(tooltip.impressions)}
            </div>
          )}
          {visibleLines.clicks && (
            <div className="text-sm text-red-600 dark:text-red-400">
              <strong>Clicks:</strong> {formatClicks(tooltip.clicks)}
            </div>
          )}
          {visibleLines.ctr && (
            <div className="text-sm text-green-600 dark:text-green-400">
              <strong>CTR:</strong> {formatCTR(tooltip.ctr)}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
